import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import './Customers.css';
import './InvoicePrint.css';

const STATUS_OPTIONS = ['Draft', 'Pending', 'Paid', 'Overdue', 'Cancelled'];

/** Maps invoice status to existing `.badge-*` classes in App.css */
function invoiceStatusBadgeClass(status) {
  const s = (status || 'Draft').toLowerCase();
  if (s === 'cancelled') return 'badge-terminated';
  return `badge-${s}`;
}

export default function InvoicePrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [inv, setInv] = useState(null);
  const [provider, setProvider] = useState(null);
  const [providerAddr, setProviderAddr] = useState(null);
  const [signatory, setSignatory] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const reload = () => api.get(`/invoices/${id}`).then(setInv).catch(e => setErr(e.message));

  useEffect(() => {
    reload();
    api.get('/settings/provider').then(setProvider).catch(() => {});
    api.get('/settings/addresses').then(rows => setProviderAddr(rows.find(r => r.is_default) || rows[0])).catch(() => {});
    api.get('/settings/signatories').then(rows => setSignatory(rows.find(r => r.is_active) || rows[0])).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (inv?.customer_id) api.get(`/customers/${inv.customer_id}`).then(setCustomer).catch(() => {});
  }, [inv]);

  const isAdmin = user?.role === 'Admin';
  const isApprover = inv && (isAdmin || inv.invoice_approver_id === user?.id);

  const changeStatus = async (newStatus) => {
    if (newStatus === inv.status) return;
    if (!window.confirm(`Change invoice status to ${newStatus}?`)) return;
    setBusy(true);
    try { await api.post(`/invoices/${id}/status`, { status: newStatus }); reload(); }
    catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  const requestDelete = async () => {
    if (!deleteReason.trim()) { alert('Please provide a reason'); return; }
    setBusy(true);
    try { await api.post(`/invoices/${id}/request-delete`, { reason: deleteReason }); setShowDeleteModal(false); setDeleteReason(''); reload(); }
    catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  const cancelDelete = async () => {
    if (!window.confirm('Withdraw the pending delete request?')) return;
    setBusy(true);
    try { await api.post(`/invoices/${id}/cancel-delete`, {}); reload(); }
    catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  if (err) {
    return (
      <div className="page-content invoice-print-page">
        <div className="card" style={{ borderColor: 'rgba(229, 62, 62, 0.35)', background: 'rgba(229, 62, 62, 0.06)' }}>
          <p style={{ margin: 0, color: 'var(--danger)' }}>Error: {err}</p>
        </div>
      </div>
    );
  }
  if (!inv) {
    return (
      <div className="page-content invoice-print-page">
        <div className="card" role="status">
          <p style={{ margin: 0, color: 'var(--text-light)' }}>Loading invoice…</p>
        </div>
      </div>
    );
  }

  const fx = Number(inv.forex_rate) || 1;
  const sub = Number(inv.subtotal || 0);
  const tax = Number(inv.tax_amount || 0);
  const total = Number(inv.total || 0);
  const adj = Number(inv.adjustment || 0);
  const calc = Number(inv.calculated_amt || 0);
  const sym = (c) => ({ INR: '₹', USD: '$', EUR: '€', GBP: '£', SGD: 'S$' }[c] || c + ' ');
  const billCcy = inv.invoice_currency || inv.agreed_currency || '';
  const agCcy = inv.agreed_currency || '';
  const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <>
      <div className="page-header no-print">
        <h1>Invoice · {inv.invoice_id}</h1>
        <div className="invoice-print-header-actions">
          <button type="button" className="btn btn-primary btn-sm" onClick={() => window.print()}>
            Print / Save as PDF
          </button>
        </div>
      </div>

      <div className="page-content invoice-print-page clause-library-page">
        <nav className="page-back-nav no-print" aria-label="Back navigation">
          <button type="button" className="page-back-link" onClick={() => navigate('/invoices?tab=invoices')}>
            ← Back to Invoices
          </button>
        </nav>

        <div className="invoice-print-context no-print" aria-label="Customer and status">
          <span className="invoice-print-context-name">{inv.client_name || customer?.legal_name || 'Customer'}</span>
          {inv.status ? (
            <span className={`badge ${invoiceStatusBadgeClass(inv.status)}`}>{inv.status}</span>
          ) : null}
        </div>

        <div className="invoice-print-summary customers-stats no-print" aria-label="Invoice summary">
          <div className="customers-stat">
            <div className="customers-stat-label">Total due</div>
            <div className="customers-stat-value tabular-nums">
              {sym(billCcy)}
              {fmt(total)}
            </div>
          </div>
          <div className="customers-stat">
            <div className="customers-stat-label">Due date</div>
            <div className="customers-stat-value">{inv.due_date || '—'}</div>
          </div>
          <div className="customers-stat">
            <div className="customers-stat-label">Invoice currency</div>
            <div className="customers-stat-value">{billCcy || '—'}</div>
          </div>
        </div>

        <div className="invoice-detail-grid">
          {/* LEFT — details + actions */}
          <div className="invoice-detail-left no-print">
            {inv.pending_delete ? (
              <div className="card invoice-sidebar-pending">
                <div className="card-header">Pending Delete Approval</div>
                <p className="invoice-sidebar-pending-body">
                  Requested by <strong>{inv.pending_delete_by_name || `User #${inv.pending_delete_by}`}</strong>. The invoice approver must approve before this invoice is removed.
                </p>
                <div className="btn-group">
                  {(inv.pending_delete_by === user?.id || isAdmin) && (
                    <button type="button" className="btn btn-outline btn-sm" onClick={cancelDelete} disabled={busy}>Withdraw Request</button>
                  )}
                </div>
              </div>
            ) : null}

            <div className="card">
              <div className="card-header">Status & Actions</div>
              <div className="invoice-sidebar-status-row">
                <span className={`badge ${invoiceStatusBadgeClass(inv.status)}`}>{inv.status || 'Draft'}</span>
              </div>
              {isApprover && (
                <div>
                  <label className="invoice-change-status-label" htmlFor="invoice-change-status-actions">Change Status</label>
                  <div id="invoice-change-status-actions" className="invoice-change-status-buttons">
                    {STATUS_OPTIONS.map(s => (
                      <button key={s} type="button"
                              className={`btn btn-sm ${s === inv.status ? 'btn-primary' : 'btn-outline'}`}
                              onClick={() => changeStatus(s)}
                              disabled={busy || s === inv.status}>{s}</button>
                    ))}
                  </div>
                </div>
              )}
              {!inv.pending_delete && (
                <div className="invoice-sidebar-delete-zone">
                  <button type="button" className="btn btn-danger btn-sm"
                          onClick={() => setShowDeleteModal(true)} disabled={busy}>
                    Request Delete (needs approval)
                  </button>
                </div>
              )}
            </div>

            <div className="card">
              <div className="card-header">Invoice Details</div>
              <div className="detail-row"><span className="lbl">Invoice ID</span><span className="val">{inv.invoice_id}</span></div>
              {inv.invoice_ref && <div className="detail-row"><span className="lbl">Reference</span><span className="val">{inv.invoice_ref}</span></div>}
              <div className="detail-row"><span className="lbl">Invoice Date</span><span className="val">{inv.invoice_date}</span></div>
              <div className="detail-row"><span className="lbl">Due Date</span><span className="val">{inv.due_date}</span></div>
              <div className="detail-row"><span className="lbl">Period</span><span className="val">{inv.period_from} → {inv.period_to}</span></div>
              <div className="detail-row"><span className="lbl">Payment Terms</span><span className="val">Net {inv.payment_terms} days</span></div>
            </div>

            <div className="card">
              <div className="card-header">Linked Records</div>
              <div className="detail-row"><span className="lbl">Customer</span><span className="val">{inv.client_name}</span></div>
              <div className="detail-row">
                <span className="lbl">SOW</span>
                <span className="val">
                  {inv.sow_id ? <Link to={`/sow/${inv.sow_id}`} className="link">{inv.sow_id}</Link> : '—'}
                </span>
              </div>
              <div className="detail-row">
                <span className="lbl">MSA</span>
                <span className="val">
                  {inv.msa_id ? <Link to={`/msa/${inv.msa_id}`} className="link">{inv.msa_id}</Link> : '—'}
                </span>
              </div>
              {inv.resource_name && <div className="detail-row"><span className="lbl">Resource</span><span className="val">{inv.resource_name}</span></div>}
            </div>

            <div className="card">
              <div className="card-header">Billing</div>
              <div className="detail-row"><span className="lbl">Model</span><span className="val">{inv.billing_model}</span></div>
              <div className="detail-row"><span className="lbl">Term</span><span className="val">{inv.billing_term}</span></div>
              <div className="detail-row"><span className="lbl">Rate</span><span className="val">{sym(agCcy)}{fmt(inv.billing_rate)}</span></div>
              <div className="detail-row"><span className="lbl">Std / Worked</span><span className="val">{inv.std_days_hrs || 0} / {inv.actual_worked || 0}</span></div>
              <div className="detail-row"><span className="lbl">Calculated</span><span className="val">{sym(agCcy)}{fmt(calc)}</span></div>
              {adj !== 0 && <div className="detail-row"><span className="lbl">Adjustment</span><span className="val">{sym(agCcy)}{fmt(adj)}</span></div>}
              <div className="detail-row"><span className="lbl">Subtotal</span><span className="val">{sym(agCcy)}{fmt(sub)}</span></div>
              {agCcy !== billCcy && <div className="detail-row"><span className="lbl">Forex (1 {agCcy})</span><span className="val">{fx} {billCcy}</span></div>}
              {tax > 0 && <div className="detail-row"><span className="lbl">Tax ({inv.gst_rate_pct || 0}%)</span><span className="val">{sym(billCcy)}{fmt(tax)}</span></div>}
              <div className="detail-row detail-row-total">
                <span className="lbl">Total ({billCcy})</span><span className="val">{sym(billCcy)}{fmt(total)}</span>
              </div>
            </div>

            {inv.notes && (
              <div className="card">
                <div className="card-header">Notes</div>
                <p className="invoice-sidebar-notes">{inv.notes}</p>
              </div>
            )}
          </div>

          {/* RIGHT — printable invoice PDF preview */}
          <div className="invoice-detail-right">
            <div className="invoice-print">
              <div className="invoice-print-header-band">
                <div className="invoice-print-brand-row">
                  <img className="invoice-print-logo" src="/logo.png" alt="" onError={e => { e.target.style.display = 'none'; }} />
                  <div>
                    <div className="invoice-print-provider-name">{provider?.provider_name || 'Colan Infotech Pvt. Ltd.'}</div>
                    <div className="invoice-print-provider-meta">
                      {providerAddr?.address_line}{providerAddr?.city ? `, ${providerAddr.city}` : ''}<br/>
                      {providerAddr?.state ? `${providerAddr.state}, ` : ''}{providerAddr?.country} {providerAddr?.pincode}<br/>
                      {providerAddr?.gst && <span>GSTIN: {providerAddr.gst}</span>}{provider?.provider_pan && <span> · PAN: {provider.provider_pan}</span>}
                    </div>
                  </div>
                </div>
                <div className="invoice-print-title-block">
                  <div className="invoice-print-title">INVOICE</div>
                  <div className="invoice-print-id">{inv.invoice_id}</div>
                  {inv.invoice_ref ? <div className="invoice-print-ref">Ref: {inv.invoice_ref}</div> : null}
                  <div className="invoice-print-status-wrap">
                    <span className={`badge ${invoiceStatusBadgeClass(inv.status)}`}>{inv.status || 'Draft'}</span>
                  </div>
                </div>
              </div>

              <div className="inv-grid">
                <div>
                  <div className="inv-h">Bill To</div>
                  <div className="invoice-print-billto-name">{inv.client_name || customer?.legal_name}</div>
                  <div className="invoice-print-billto-meta">
                    {customer?.billing_address || customer?.registered_address}<br/>
                    {customer?.country}<br/>
                    {customer?.gst_vat && <>GST/VAT: {customer.gst_vat}<br/></>}
                    {customer?.contact_email && <>Contact: {customer.primary_contact} · {customer.contact_email}</>}
                  </div>
                </div>
                <div>
                  <div className="inv-h">Invoice Details</div>
                  <table className="invoice-print-mini-table">
                    <tbody>
                      <tr><td>Invoice Date</td><td className="inv-mini-strong">{inv.invoice_date}</td></tr>
                      <tr><td>Due Date</td><td className="inv-mini-strong">{inv.due_date}</td></tr>
                      <tr><td>Payment Terms</td><td>Net {inv.payment_terms} days</td></tr>
                      <tr><td>Period</td><td>{inv.period_from} → {inv.period_to}</td></tr>
                      <tr><td>SOW Reference</td><td className="inv-mini-strong">{inv.sow_id}</td></tr>
                      <tr><td>MSA Reference</td><td>{inv.msa_id}</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <table className="inv-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th className="inv-th-right">Std/Worked</th>
                    <th className="inv-th-right">Rate</th>
                    <th className="inv-th-right">Amount ({agCcy})</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <div className="inv-desc-title">{inv.resource_name || '—'}</div>
                      <div className="inv-desc-meta">
                        {inv.billing_model} · {inv.billing_term}<br/>
                        Project: {inv.sow_id}
                      </div>
                    </td>
                    <td className="inv-td-right">{inv.actual_worked || 0} / {inv.std_days_hrs || 0}</td>
                    <td className="inv-td-right">{sym(agCcy)}{fmt(inv.billing_rate)}</td>
                    <td className="inv-td-right inv-mini-strong">{sym(agCcy)}{fmt(calc)}</td>
                  </tr>
                  {adj !== 0 && (
                    <tr>
                      <td><span className="inv-adj-note">Adjustment</span></td>
                      <td colSpan={2} />
                      <td className="inv-td-right">{sym(agCcy)}{fmt(adj)}</td>
                    </tr>
                  )}
                </tbody>
              </table>

              <div className="inv-totals">
                <div className="row"><span>Subtotal ({agCcy})</span><span>{sym(agCcy)}{fmt(sub)}</span></div>
                {fx && fx !== 1 && billCcy !== agCcy && (
                  <div className="row inv-totals-row-forex">
                    <span>Forex (1 {agCcy} = {fx} {billCcy})</span><span>×{fx}</span>
                  </div>
                )}
                {tax > 0 && <div className="row"><span>Tax ({inv.gst_rate_pct || 0}%)</span><span>{sym(billCcy)}{fmt(tax)}</span></div>}
                <div className="row total"><span>Total Due</span><span>{sym(billCcy)}{fmt(total)}</span></div>
              </div>

              {inv.notes && (
                <div className="invoice-print-notes-box">
                  <div className="inv-h">Notes</div>
                  <div className="invoice-print-notes-body">{inv.notes}</div>
                </div>
              )}

              <div className="invoice-print-signatures">
                <div>
                  <div className="invoice-print-sign-label">Authorised Signatory</div>
                  <div className="invoice-print-sign-name">{signatory?.name || 'Authorised Signatory'}</div>
                  <div className="invoice-print-sign-title">{signatory?.title}</div>
                </div>
                <div className="invoice-print-footer-note">
                  This is a computer-generated invoice. No physical signature required for transmission.<br/>
                  For queries, contact <strong>{signatory?.email || 'accounts@colaninfotech.com'}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Request Invoice Deletion</h2>
            <p className="invoice-modal-hint">
              The invoice approver must approve before <strong>{inv.invoice_id}</strong> is permanently removed. The invoice stays unchanged until then.
            </p>
            <div className="form-group">
              <label>Reason for deletion</label>
              <textarea rows={3} value={deleteReason} onChange={e => setDeleteReason(e.target.value)} placeholder="Why does this invoice need to be deleted?" />
            </div>
            <div className="btn-group mt-4">
              <button className="btn btn-primary" onClick={requestDelete} disabled={busy || !deleteReason.trim()}>Submit Request</button>
              <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
